import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/use-toast'
import { integrationService, type CoostoConfig } from '@/lib/services/integrationService'
import CoostoLogo from '@/assets/coosto.svg'

const coostoSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required'),
  baseUrl: z.string().url('Must be a valid URL').min(1, 'Base URL is required'),
})

type CoostoFormData = z.infer<typeof coostoSchema>

interface CoostoConfigDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function CoostoConfigDialog({ open, onClose, onSuccess }: CoostoConfigDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  // Fetch existing configuration
  const { data: existingConfig } = useQuery({
    queryKey: ['integration', 'coosto', 'config'],
    queryFn: () => integrationService.getIntegration('coosto', true),
    enabled: open,
    retry: false,
    throwOnError: false,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CoostoFormData>({
    resolver: zodResolver(coostoSchema),
    defaultValues: {
      apiKey: '',
      baseUrl: '',
    },
  })

  // Update form when existingConfig arrives
  useEffect(() => {
    if (existingConfig?.config) {
      const config = existingConfig.config as Record<string, unknown>

      // Try both camelCase and snake_case keys since the API might return either
      reset({
        apiKey: (config.apiKey || config.api_key || '') as string,
        baseUrl: (config.baseUrl || config.base_url || '') as string,
      })
    }
  }, [existingConfig, reset])

  // Configure mutation
  const configureMutation = useMutation({
    mutationFn: (data: CoostoConfig) => integrationService.configureCoosto(data),
    onSuccess: () => {
      toast({
        title: t('integration.configured'),
        description: t('integration.coosto_success'),
      })
      onSuccess()
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('integration.configure_error'),
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: CoostoFormData) => {
    configureMutation.mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <img src={CoostoLogo} alt="Coosto" className="h-8 w-8" />
            <DialogTitle>{t('integration.configure_coosto')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('integration.coosto_description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* API Key */}
          <div>
            <Label htmlFor="apiKey">{t('integration.coosto_api_key')}</Label>
            <Input
              id="apiKey"
              type="password"
              {...register('apiKey')}
              placeholder="xxxxxxxxxxxxxxxxxxxxxx"
            />
            {errors.apiKey && (
              <p className="text-sm text-red-600 mt-1">{errors.apiKey.message}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t('integration.coosto_api_key_help')}
            </p>
          </div>

          {/* Base URL */}
          <div>
            <Label htmlFor="baseUrl">{t('integration.coosto_base_url')}</Label>
            <Input
              id="baseUrl"
              {...register('baseUrl')}
              placeholder="https://api.coosto.com/v2/"
            />
            {errors.baseUrl && (
              <p className="text-sm text-red-600 mt-1">{errors.baseUrl.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={configureMutation.isPending}>
              {configureMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
